import { useState } from 'react';
import { useSelector } from 'react-redux';
import type { AppState } from '@nextgen-web-framework/core';
import {
  ButtonStyles,
  ButtonSize,
  ButtonWidth,
  useTranslation,
  useBreakpoint,
  AccountType,
} from '@nextgen-web-framework/all';
import {
  DynamicObjectInterface,
  RegistrationFlowDataType,
  CoAppDocumentDocumentTypeEnum,
  ImageReturnType,
} from '@commerce-profile-webcomponents/sdk';
import ImageUpload from '../../ImageUpload/ImageUpload';
import DesktopLayout from '../../Common/DesktopLayout/DesktopLayout';
import ButtonWithLoading from '../../Common/ButtonWithLoading/ButtonWithLoading';
import { EDITOR_ASPECT_RATIO } from '../../../utils/Constants';
import { CoAppCommonProps } from '../types/propTypes';
import styles from './CoApplicantDocumentUploadStep.module.scss';

const CoApplicantDocumentUploadStep = ({ nextHandler }: CoAppCommonProps) => {
  const t = useTranslation();
  const breakpoint = useBreakpoint();

  const registrationData = useSelector(
    (state: AppState) => state.commerceProfile.registrationFlowData,
  ) as RegistrationFlowDataType;
  const { userData, OtpData } = registrationData;

  const registrationCmsData = useSelector((state: AppState) => state.commerceProfile.registrationCmsData);
  const { coApp } = registrationCmsData;
  const { marriageCertImage } = coApp as DynamicObjectInterface;
  const accountTypeImagesCmsData = useSelector((state: AppState) => state.commerceProfile.accountTypeImagesCMS);
  const selectedAccountType: AccountType = useSelector((state: AppState) => state.commerceProfile.selectedAccountType);

  const idVerificationImage = selectedAccountType
    ? accountTypeImagesCmsData?.[selectedAccountType]?.idVerificationImage
    : undefined;
  const idStepDesktopImage = idVerificationImage?.find((item: ImageReturnType) => item?.viewport.includes(breakpoint));

  const [uploadedDocument, setUploadedDocument] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const patchHandler = async () => {
    setIsLoading(true);
    const payload: DynamicObjectInterface = {
      coAppDocuments: [
        {
          documentData: uploadedDocument,
          documentType: CoAppDocumentDocumentTypeEnum.MarriageCertificate,
        },
      ],
      otpRequestId: OtpData.challengeId,
      version: userData.patchVersion ?? 0,
    };
    // Seq #57 - patch upload document
    await Promise.resolve(nextHandler?.(payload, undefined, false, true));
  };

  const uploadImageHandler = (image: string, _setShowLoading?: Function, setUploadWasSuccess?: Function) => {
    const imageBase64 = image.split(',');
    setUploadedDocument(imageBase64[1]);
    setUploadWasSuccess?.(true);
  };

  const removeImageHandler = () => {
    setUploadedDocument('');
  };

  return (
    <DesktopLayout desktopImage={idStepDesktopImage} containerClassName={styles['co-app-document-layout']}>
      <div className={styles['co-app-document-container']} id="co-app-document-container">
        <div className={styles['co-app-document']} data-testid="co-app-document-testid">
          <div className={styles['co-app-document-header']}>{t('profile.coApp.supportingDocument')}</div>
          <div className={styles['co-app-document-desc']}>{t('profile.coApp.supportingDocumentText')}</div>
          <div className={styles['co-app-document-form']}>
            <ImageUpload
              uploadLabel={t('profile.ImageUpload.uploadLabel')}
              callback={uploadImageHandler}
              sampleImage={marriageCertImage}
              showImage={true}
              removeIconHandler={removeImageHandler}
              editorAspectRatio={EDITOR_ASPECT_RATIO.PASSPORT}
              wrapperClass={styles['imageWrapperClass']}
            />
          </div>
          <div className={styles['co-app-document-next-btn']}>
            <ButtonWithLoading
              title={t('profile.common.nextButton')}
              data-testid="next-btn-testid"
              buttonStyle={ButtonStyles.SOLID}
              size={ButtonSize.MEDIUM}
              disabled={!uploadedDocument}
              onClick={patchHandler}
              buttonWidth={ButtonWidth.FULL_WIDTH}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>
    </DesktopLayout>
  );
};

export default CoApplicantDocumentUploadStep;
